import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Flex } from '@adobe/react-spectrum';

import { BaseDeckPane } from './BaseDeckPane';
import { getGrades } from '../../../data/ScoreDisplayUtils';
import { SaltBreakdown } from './SaltBreakdown';
import './saltPane.css';

const CATEGORY_CONSTANTS = {
    annihilator: { label: 'Annihilator', chartLabel: 'Annihilator' },
    boardwipes: { label: 'Board Wipes', chartLabel: 'Board Wipes' },
    cant: { label: `You can't...`, chartLabel: `Uh… you can't do that` },
    mld: { label: 'MLD', chartLabel: 'Mass Land Denial' },
    taxes: { label: 'Taxes', chartLabel: 'Tax effects' },
    payTheOne: { label: 'Pay the…?', chartLabel: 'Do you pay the…?' },
    cardPrice: { label: 'Card Price', chartLabel: 'Card Price' },
    cedhMetashare: { label: 'cEDH Staples', chartLabel: 'cEDH Staples', noChart: true },
    sacrifice: { label: 'Sacrifice', chartLabel: 'Sacrifice' },
    infiniteCombos: { label: 'Infinite Combos', chartLabel: 'Infinite combos' },
    reserved: { label: 'Reserved List', chartLabel: 'Reserved List cards', noChart: true },
    locks: { label: 'Locks', chartLabel: 'Locks' },
    theft: { label: 'Theft', chartLabel: 'Theft' },
    chaos: { label: 'Chaos', chartLabel: 'Chaos effects' },
    groupSlug: { label: 'Group Slug', chartLabel: 'Group Slug' },
    extraTurns: { label: 'Extra Turns', chartLabel: 'Extra Turns' },
    extraCombats: { label: 'Extra Combats', chartLabel: 'Extra Combats' },
    poison: { label: 'Poison', chartLabel: 'Poison' },
    wheels: { label: 'Wheels', chartLabel: 'Wheels' },
    discard: { label: 'Discard', chartLabel: 'Discard' },
    offColorFetches: { label: 'Fetches', chartLabel: 'Off Color Fetches' },
};

const SUBCATEGORY_CONSTANTS = {
    cant: {
        cantActivate: { label: 'Activate' },
        cantAttack: { label: 'Attack' },
        cantCast: { label: 'Cast' },
        cantDraw: { label: 'Draw' },
        cantEven: { label: 'Even' },
        cantGainLife: { label: 'Gain Life' },
        cantSearch: { label: 'Search' },
        cantTrigger: { label: 'Trigger' },
        cantUntap: { label: 'Untap' },
    },
};

export function SaltPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const getCategories = () => {
        const list = {};
        const categoryKeys = Object.keys(CATEGORY_CONSTANTS).sort((a, b) =>
            CATEGORY_CONSTANTS[a].label.toLowerCase() < CATEGORY_CONSTANTS[b].label.toLowerCase() ? -1 : 1
        );

        categoryKeys.forEach((category) => {
            const has = deck?.details?.salt?.scoring?.hasOwnProperty(category);
            if (!has && category !== 'cant') return;

            const item = deck?.details?.salt?.scoring?.[category];
            const items = Object.keys(item?.list || []).map((key) => {
                const entry = item.list[key];
                if (
                    category?.toLowerCase()?.includes('combos') ||
                    category?.toLowerCase()?.includes('locks')
                ) {
                    return {
                        ...entry,
                        name: entry?.name?.toString()?.replace(/,(?! )/g, '\n'),
                    };
                }
                return {
                    ...entry,
                    ...cards?.[key],
                };
            });

            list[category] = {
                label: CATEGORY_CONSTANTS[category].label,
                id: category,
                count: items?.length || 0,
                list: items,
                color: '#FFFFFF',
                subCategories: {},
                chartLabel: CATEGORY_CONSTANTS[category].chartLabel,
                noChart: !!CATEGORY_CONSTANTS[category].noChart,
            };

            if (category === 'cant') {
                Object.keys(SUBCATEGORY_CONSTANTS[category] || {}).forEach((subCategory) => {
                    if (!deck?.details?.salt?.scoring?.[subCategory]) return;
                    const subItem = deck?.details?.salt?.scoring?.[subCategory];
                    const subItems = subItem?.list || [];
                    list[category].subCategories[subCategory] = {
                        label: SUBCATEGORY_CONSTANTS[category][subCategory].label,
                        id: subCategory,
                        count: Object.keys(subItems).length,
                        list: Object.keys(subItems).map((_key) => ({
                            ...subItems[_key],
                            ...cards?.[_key],
                        })),
                        parentCategory: category,
                    };
                    list[category].count += Object.keys(subItems).length;
                    list[category].list = list[category].list.concat(
                        list[category].subCategories[subCategory].list
                    );
                });
            }
        });

        if (list['cant']?.count < 1) {
            delete list['cant'];
        }

        return list;
    };

    const categories = useMemo(() => getCategories(), [deck, cards]);

    const saltPercentageScore = getGrades(deck)?.salt?.percentage ?? 0;

    return (
        <BaseDeckPane title="Salt" content={<div />} showScore={true} categories={categories}>
            <Flex direction="column" rowGap={16} marginTop={10}>
                <SaltBreakdown
                    categories={categories}
                    deckTotal={deck?.details?.salt?.total}
                    personality={deck?.details?.salt?.personality}
                    profile={deck?.details?.salt?.profile}
                    saltPercentage={saltPercentageScore}
                />
            </Flex>
        </BaseDeckPane>
    );
}
